/**
 * `/capture` — PWA share target landing and deep link. The service worker stores shared
 * text/files (POST /capture/share) in a cache; this route picks them up and opens the capture
 * sheet over Now.
 */
import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useAppUi } from '../components/app-ui.js'
import { isEnabled } from '../lib/flags.js' // ia-shell
import { toPending } from '../lib/uploads.js'
import { NowPage } from './now.js'

export const SHARE_CACHE = 'viko-share'

export async function takeShared(): Promise<{ text: string; files: File[] }> {
  if (!('caches' in globalThis)) return { text: '', files: [] }
  const cache = await caches.open(SHARE_CACHE)
  const meta = await cache.match('/capture/share/meta')
  if (!meta) return { text: '', files: [] }
  const m = (await meta.json()) as {
    text: string
    files: { key: string; name: string; type: string }[]
  }
  const files: File[] = []
  for (const f of m.files) {
    const r = await cache.match(f.key)
    if (r) files.push(new File([await r.blob()], f.name, { type: f.type }))
    await cache.delete(f.key)
  }
  await cache.delete('/capture/share/meta')
  return { text: m.text, files }
}

export function CapturePage() {
  const ui = useAppUi()
  const nav = useNavigate()
  useEffect(() => {
    let alive = true
    const params = new URLSearchParams(location.search)
    void (async () => {
      const shared = params.has('shared')
        ? await takeShared()
        : { text: params.get('text') ?? '', files: [] }
      if (!alive) return
      ui.openCapture({ text: shared.text, files: toPending(shared.files) })
      void nav({ to: isEnabled('lanesTab') ? '/home' : '/now', replace: true })
    })()
    return () => {
      alive = false
    }
  }, [ui, nav])
  return <NowPage />
}
